import { Button, useDisclosure } from "@chakra-ui/react";
import AddTransactionAllocationOverrideModal from "features/fund-family/setup/fund/allocations/TransactionOverrideAllocationCreateModal";
import { FundFamilySetupTabProps } from "features/fund-family/setup/FundFamilySetupTabProps";
import { useFundFamilyTransactionAllocationOverrideSuspense } from "gen/endpoints/allocation-rule-lookup/allocation-rule-lookup";
import { useFundFamilyConfigRetrieveSuspense } from "gen/endpoints/fund-family-config/fund-family-config";
import { useTransactionCalculationRetrieveSuspense } from "gen/endpoints/transactions/transactions";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import { PageInstructions } from "shared/components/layout/PageInstructions";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";

export const FundFamilySetupFundTabAllocations: React.FC = () => {
  const { t } = useTranslation(["allocation", "common"]);
  const { fundFamilyId } = useOutletContext<FundFamilySetupTabProps>();

  const { data: tableData, refetch } =
    useFundFamilyTransactionAllocationOverrideSuspense(fundFamilyId);

  const { data: fundFamilyConfigData } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);

  const { data: defaultCalculationData } =
    useTransactionCalculationRetrieveSuspense(
      fundFamilyId,
      fundFamilyConfigData.config.default_allocation_calculation_id!,
    );
  const {
    isOpen: isSetupModalOpen,
    onOpen: openSetupModal,
    onClose: closeSetupModal,
  } = useDisclosure();

  const addButton = (
    <Restricted permission={PermissionTypeEnum.service}>
      <Button size="xs" onClick={openSetupModal}>
        {t("allocation:transactionOverride.addRule")}
      </Button>
    </Restricted>
  );

  return (
    <PageTabBody>
      <PageInstructions>
        {t("allocation:transactionOverride.currentAllocation", {
          calculation_name: defaultCalculationData.transaction_calculation.name,
        })}
      </PageInstructions>
      <TableBox>
        <TableBoxHeader>
          <TableBoxTitle>
            {t("allocation:transactionOverride.title")}
          </TableBoxTitle>
          <DataTableBoxActions createAction={addButton} />
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable
            data={tableData}
            emptyStateProps={{
              title: t("allocation:transactionOverride.emptyTitle"),
              children: addButton,
            }}
          />
        </TableBoxBody>
      </TableBox>
      {isSetupModalOpen && (
        <AddTransactionAllocationOverrideModal
          fundFamilyId={fundFamilyId}
          onCloseComplete={closeSetupModal}
          onSuccess={refetch}
        />
      )}
    </PageTabBody>
  );
};
